import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import ReactGoogleMapLoader from 'react-google-maps-loader';
import ReactGooglePlacesSuggest from 'react-google-places-suggest';

import { useDispatch, useSelector } from 'react-redux';
import { findStateLabelByValue } from '../../util/helpers/countries';
import { getStates } from '../../store/actions/common/commonActions';
import {
  selectCountries,
  selectStates,
} from '../../store/selectors/commonSelectors';
import { getValidationSchema } from '../../util/helpers/getValidationSchema';
import {
  formatPhoneNumberAsString,
  phoneNumberToObjectHelper,
  validatePhoneNumber,
} from '../../util/helpers/phoneNumberHelpers';
import Paragraph from '../Paragraph/Paragraph';
import useIraApplicationFundingData from '../../hooks/useApplicationIraFundingData';
import { selectPortalGatewayISOCodesListToIgnoreAreaCode } from '../../store/selectors/settingsSelectors';
import useCustodianInformationDispatch from '../../hooks/useCustodianInformationDispatch';
import Back from '../Buttons/Back';
import { ModalHeading } from '../Modal';
import { FormContainer, FormWrap } from '../FormContainer/FormContainer';
import InputText from '../Fields/InputText';
import Select from '../Fields/Select';
import OnBoardingCta from '../OnBoarding/OnBoardingCta';
import PhoneField from '../Fields/PhoneField';
import themeColors from '../../assets/styles/themeColors';

const SuggestItemWrapper = styled.div`
  padding: 15px;
`;

const HiddenInput = styled.input`
  opacity: 0;
  cursor: 'context-menu';
  width: 0;
  height: 0;
`;

const ManualCustodianForm = ({
  handleNext,
  setIsManual,
  isManual,
  shouldClearForm,
  setShouldClearForm,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [search, setSearch] = useState('');
  const [address, setAddress] = useState({});
  const states = useSelector(selectStates);
  const { custodianInformationDispatch } = useCustodianInformationDispatch();

  const { IraApplicationFundingResponse } = useIraApplicationFundingData();
  const { IRAFundingCustodianResponse } = IraApplicationFundingResponse;

  const countries = useSelector(selectCountries);
  const selectedISOCodesListToIgnoreAreaCode = useSelector(
    selectPortalGatewayISOCodesListToIgnoreAreaCode,
  );
  const searchOptions = {
    componentRestrictions: { country: 'us' },
  };
  const [stateValue, setStateValue] = useState('');

  const formValidationSchema = Yup.object({
    ...getValidationSchema({
      fields: [
        'CustodianName',
        'City',
        'Street',
        'State',
        'AptOrSuite',
        'PostalCode',
      ],
      countryCode: 'USA',
    }),
  });

  const STATE_OPTIONS = [
    ...states?.map((state) => ({
      value: state?.Code,
      label: state?.Caption,
    })),
  ];

  const {
    handleSubmit,
    control,
    setValue,
    setError,
    formState: { errors },
    getValues,
  } = useForm({
    resolver: yupResolver(formValidationSchema),
  });

  const setFormValue = (label, value) => {
    if (value) {
      setValue(label, value, { shouldDirty: true });
    }
  };

  const updateFormValues = (formData) => {
    if (formData) {
      const { Name, Address, Fax, Phone } = formData;

      if (Address) {
        const { AptOrSuite, City, Street, State, PostalCode } = Address;

        setFormValue('Street', Street);

        setFormValue('AptOrSuite', AptOrSuite);

        setFormValue('City', City);

        setFormValue('PostalCode', PostalCode);

        if (State) {
          setFormValue('State', {
            value: State,
            label: findStateLabelByValue(states, State),
          });
        }
      }

      if (Name) {
        setFormValue('CustodianName', Name);
      }

      if (Fax?.Iso3CountryCode && countries) {
        const PhoneNumber = formatPhoneNumberAsString(
          Fax,
          countries,
          selectedISOCodesListToIgnoreAreaCode,
        );

        setFormValue('CustodianFax', PhoneNumber);
      }

      if (Phone?.Iso3CountryCode && countries) {
        const PhoneNumber = formatPhoneNumberAsString(
          Phone,
          countries,
          selectedISOCodesListToIgnoreAreaCode,
        );

        setFormValue('CustodianPhone', PhoneNumber);
      }
    }
  };

  useEffect(() => {
    dispatch(getStates({ countryCode: 'USA' }));
  }, [dispatch]);

  useEffect(() => {
    if (shouldClearForm && isManual) {
      updateFormValues(IRAFundingCustodianResponse);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [IRAFundingCustodianResponse, countries]);

  useEffect(() => {
    updateFormValues({
      Address: address,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  useEffect(() => {
    if (shouldClearForm && isManual) {
      if (IRAFundingCustodianResponse) {
        const { Address } = IRAFundingCustodianResponse;

        if (Address?.State) {
          setFormValue('State', {
            value: Address?.State,
            label: findStateLabelByValue(states, Address?.State),
          });
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [IRAFundingCustodianResponse, states]);

  useEffect(() => {
    setFormValue('State', {
      label: findStateLabelByValue(states, stateValue),
      value: stateValue,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateValue, states]);

  const processAddress = (addressDetails) => {
    const addressDetailTypeMap = {
      subpremise: 'AptOrSuite',
      street_number: 'Street',
      premise: 'AptOrSuite',
      route: 'Street',
      locality: 'City',
      postal_town: 'City',
      administrative_area_level_1: 'State',
      postal_code: 'PostalCode',
    };

    const addressMap = {
      Street: {
        prop: 'short_name',
        values: [],
      },
      AptOrSuite: {
        prop: 'short_name',
        values: [],
      },
      City: {
        prop: 'long_name',
        values: [],
      },
      State: {
        prop: 'short_name',
        values: [],
      },
      PostalCode: {
        prop: 'short_name',
        values: [],
      },
    };

    addressDetails.forEach((detail) => {
      detail.types.forEach((type) => {
        if (addressDetailTypeMap[type]) {
          const { prop, process, values } = addressMap[
            addressDetailTypeMap[type]
          ];
          values.push(process ? process(detail[prop]) : detail[prop]);
        }
      });
    });

    setAddress(
      Object.fromEntries(
        Object.entries(addressMap).map(([key, { values }]) => [
          key,
          values.join(' '),
        ]),
      ),
    );
  };

  const handleSelectSuggest = (geocodedPrediction) => {
    setSearch('');
    processAddress(geocodedPrediction.address_components);
  };
  const handleChangeState = (event) => {
    setStateValue(event.target.value);
  };
  const onSubmit = ({
    CustodianName,
    Street,
    AptOrSuite,
    City,
    State,
    PostalCode,
    CustodianPhone,
    CustodianFax,
  }) => {
    if (CustodianPhone && validatePhoneNumber(CustodianPhone)) {
      setError('CustodianPhone', {
        type: 'manual',
        message: t('fields.errors.validation.custodianPhoneIsInvalid'),
      });
      return;
    }

    if (CustodianFax && validatePhoneNumber(CustodianFax)) {
      setError('CustodianFax', {
        type: 'manual',
        message: t('fields.errors.validation.custodianFaxIsInvalid'),
      });
      return;
    }

    const CustodianPhoneFormatted = phoneNumberToObjectHelper(CustodianPhone);
    const CustodianFaxFormatted = phoneNumberToObjectHelper(CustodianFax);

    custodianInformationDispatch({
      data: {
        Name: CustodianName,
        Address: {
          Street,
          AptOrSuite,
          City,
          CountryIso3Code: 'USA',
          PostalCode,
          State: State.value,
        },
        Phone: {
          ...CustodianPhoneFormatted,
        },
        Fax: {
          ...CustodianFaxFormatted,
        },
      },
      defaultValues: IRAFundingCustodianResponse,
      onSuccess: handleNext,
    });
  };

  return (
    <>
      {setIsManual && (
        <Back
          onClick={() => {
            setIsManual(false);
            setShouldClearForm(false);
          }}
        />
      )}
      <ModalHeading>{t('iraCustodian.details.title')}</ModalHeading>
      <Paragraph mb={24} color={themeColors.colorTextXLLight}>
        {t('iraCustodian.details.description')}
      </Paragraph>
      <FormWrap>
        <FormContainer twoColumns>
          <Controller
            control={control}
            render={({ field, fieldState }) => (
              <InputText
                label={t('fields.labels.custodianName')}
                {...field}
                {...fieldState}
                autoFocus
              />
            )}
            name="CustodianName"
          />
          <ReactGoogleMapLoader
            params={{
              key: process.env.REACT_APP_MAPS_API_KEY,
              libraries: 'places',
            }}
            render={(googleMaps) =>
              googleMaps && (
                <ReactGooglePlacesSuggest
                  googleMaps={googleMaps}
                  displayPoweredByGoogle={false}
                  autocompletionRequest={{
                    input: search,
                  }}
                  searchOptions={searchOptions}
                  onSelectSuggest={handleSelectSuggest}
                  textNoResults={t('secureAddressEnter.notFound')}
                  customRender={(prediction) => (
                    <SuggestItemWrapper>
                      {prediction
                        ? prediction.description
                        : t('secureAddressEnter.notFound')}
                    </SuggestItemWrapper>
                  )}
                >
                  <Controller
                    control={control}
                    render={({ field: { onChange, ...field }, fieldState }) => (
                      <InputText
                        {...field}
                        {...fieldState}
                        onChange={(e) => {
                          onChange(e);
                          setSearch(e.target.value);
                        }}
                        placeholder="Street Address"
                        autocomplete="address-line1"
                        label={t('secureAddressEnter.street')}
                      />
                    )}
                    name="Street"
                  />
                </ReactGooglePlacesSuggest>
              )
            }
          />
          <Controller
            control={control}
            render={({ field, fieldState }) => (
              <InputText
                {...field}
                {...fieldState}
                label={t('secureAddressEnter.aptOrSuite')}
                autocomplete="address-line2"
              />
            )}
            name="AptOrSuite"
          />
          <Controller
            control={control}
            render={({ field, fieldState }) => (
              <InputText
                {...field}
                {...fieldState}
                label={t('secureAddressEnter.city')}
                autocomplete="address-level2"
              />
            )}
            name="City"
          />
          <HiddenInput
            type="text"
            autoComplete="address-level1"
            value={stateValue}
            onChange={handleChangeState}
          />
          <Controller
            control={control}
            render={({ field: { ref, ...field }, fieldState }) => (
              <Select
                label={t('secureAddressEnter.state')}
                options={STATE_OPTIONS}
                {...field}
                {...fieldState}
                isSearchable={false}
              />
            )}
            name="State"
          />

          <Controller
            control={control}
            render={({ field, fieldState }) => (
              <InputText
                {...field}
                {...fieldState}
                label={t('secureAddressEnter.postalCode')}
              />
            )}
            name="PostalCode"
            autocomplete="postal-code"
          />
          <Controller
            control={control}
            render={({ field, fieldState }) => (
              <PhoneField
                {...field}
                {...fieldState}
                label={t('fields.labels.custodianPhone')}
                setValue={setValue}
                errors={errors}
                defaultValue={getValues('CustodianPhone')}
              />
            )}
            name="CustodianPhone"
          />
          <Controller
            control={control}
            render={({ field, fieldState }) => (
              <PhoneField
                {...field}
                {...fieldState}
                label={t('fields.labels.custodianFax')}
                setValue={setValue}
                errors={errors}
                defaultValue={getValues('CustodianFax')}
              />
            )}
            name="CustodianFax"
          />
        </FormContainer>
        <OnBoardingCta type="submit" onClick={handleSubmit(onSubmit)} />
      </FormWrap>
    </>
  );
};

ManualCustodianForm.propTypes = {
  handleNext: PropTypes.func,
  isManual: PropTypes.bool,
  setIsManual: PropTypes.func,
  shouldClearForm: PropTypes.bool,
  setShouldClearForm: PropTypes.bool,
};

export default ManualCustodianForm;
